import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldAlert, Check, Trash2, X, Loader2, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LABELS = {
    arcade: "Arcade leaderboard",
    quest: "Quest submissions",
    ride: "Ride Call",
    trail_report: "Trail reports",
    community: "Community photos",
};

function summarize(kind, item) {
    if (kind === "arcade") return { title: item.name, subtitle: `Stage ${item.stage} · Score ${item.score}` };
    if (kind === "quest") return { title: item.user_name, subtitle: `${item.trail_slug} — "${item.note || "—"}"` };
    if (kind === "ride") return { title: item.host_name, subtitle: `${item.meeting_point || ""} — "${item.notes || ""}"` };
    if (kind === "trail_report") return { title: item.user_name, subtitle: `${item.trail_slug} — "${item.comment || ""}"` };
    if (kind === "community") return { title: item.user_name, subtitle: `"${item.caption || "(nessuna didascalia)"}"` };
    return { title: "Item", subtitle: "" };
}

export default function AdminModeration() {
    const { user, checking } = useAuth();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [actingId, setActingId] = useState(null);

    useEffect(() => {
        if (!checking && (!user || user.role !== "admin")) nav("/login");
    }, [user, checking, nav]);

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/admin/moderation/pending");
            setData(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore caricamento");
        } finally {
            setBusy(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const doAction = async (kind, id, action) => {
        setActingId(id);
        try {
            if (action === "delete") {
                await api.delete(`/admin/moderation/${kind}/${id}`);
                toast.success("Eliminato");
            } else {
                await api.post(`/admin/moderation/${kind}/${id}/${action}`);
                toast.success(action === "approve" ? "Approvato" : "Respinto");
            }
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore");
        } finally {
            setActingId(null);
        }
    };

    const scan = async () => {
        if (!window.confirm("Ri-scansiona tutto il database alla ricerca di parolacce esistenti?")) return;
        setScanning(true);
        try {
            const { data } = await api.post("/admin/moderation/scan");
            toast.success(`Scan completata · ${data.flagged.total_flagged} elementi flaggati`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore scan");
        } finally {
            setScanning(false);
        }
    };

    if (checking) return <div className="p-20 text-zinc-500">…</div>;
    if (!user || user.role !== "admin") return null;

    const kinds = ["arcade", "quest", "ride", "trail_report", "community"];

    return (
        <div data-testid="admin-moderation-page" className="max-w-[1200px] mx-auto px-5 md:px-10 py-14">
            <Link to="/admin" className="text-xs uppercase tracking-[0.3em] text-zinc-400 hover:text-abnormal-red inline-flex items-center gap-2">
                <ArrowLeft size={14} /> Admin
            </Link>

            <div className="mt-6 flex items-center gap-3 flex-wrap">
                <ShieldAlert className="text-abnormal-red" />
                <h1 className="font-display text-4xl md:text-6xl uppercase leading-none">Moderazione contenuti</h1>
            </div>
            <p className="text-zinc-400 mt-4 max-w-2xl text-sm">
                Contenuti flaggati dal filtro anti-parolacce. Sono salvati nel DB ma NON visibili al pubblico finché un admin non li approva o elimina.
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
                <button
                    data-testid="moderation-refresh"
                    onClick={load}
                    disabled={busy}
                    className="text-xs uppercase tracking-[0.25em] font-bold border border-white/20 px-4 py-2 hover:border-white inline-flex items-center gap-2"
                >
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <RotateCw size={14} />} Ricarica
                </button>
                <button
                    data-testid="moderation-scan"
                    onClick={scan}
                    disabled={scanning}
                    className="text-xs uppercase tracking-[0.25em] font-bold border border-abnormal-red text-abnormal-red px-4 py-2 hover:bg-abnormal-red hover:text-white inline-flex items-center gap-2"
                >
                    {scanning ? <Loader2 size={14} className="animate-spin" /> : <ShieldAlert size={14} />} Scansiona DB esistente
                </button>
                {data && (
                    <div className="text-xs uppercase tracking-[0.25em] font-bold px-4 py-2 bg-abnormal-red text-white">
                        TOTALE FLAGGATI: {data.total || 0}
                    </div>
                )}
            </div>

            <div className="mt-10 space-y-10">
                {data && kinds.map((kind) => {
                    const bucket = data[kind] || { items: [], count: 0 };
                    return (
                        <section key={kind} data-testid={`moderation-section-${kind}`}>
                            <h2 className="font-display text-xl md:text-2xl uppercase mb-3 flex items-baseline gap-3">
                                {LABELS[kind]}
                                <span className="text-xs text-zinc-500 font-sans tracking-[0.2em]">({bucket.count})</span>
                            </h2>
                            {bucket.count === 0 ? (
                                <p className="text-zinc-600 text-sm italic">Nessun contenuto flaggato.</p>
                            ) : (
                                <div className="border border-white/10 divide-y divide-white/10">
                                    {bucket.items.map((item) => {
                                        const s = summarize(kind, item);
                                        const isActing = actingId === item.id;
                                        return (
                                            <div key={item.id} className="p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-5 hover:bg-white/5">
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-display text-lg uppercase truncate">{s.title}</p>
                                                    <p className="text-sm text-zinc-400 break-words">{s.subtitle}</p>
                                                    <p className="text-[10px] tracking-[0.2em] uppercase text-abnormal-red mt-2">
                                                        Parolacce trovate: {Array.isArray(item.moderation_hits) ? item.moderation_hits.join(", ") : "—"}
                                                    </p>
                                                </div>
                                                <div className="flex gap-2 flex-shrink-0">
                                                    <button
                                                        data-testid={`moderation-approve-${item.id}`}
                                                        disabled={isActing}
                                                        onClick={() => doAction(kind, item.id, "approve")}
                                                        title="Approva (mostra al pubblico)"
                                                        className="p-2 border border-white/20 text-zinc-300 hover:border-green-500 hover:text-green-500 disabled:opacity-50"
                                                    >
                                                        {isActing ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                                                    </button>
                                                    <button
                                                        data-testid={`moderation-reject-${item.id}`}
                                                        disabled={isActing}
                                                        onClick={() => doAction(kind, item.id, "reject")}
                                                        title="Respingi (resta nel DB, nascosto)"
                                                        className="p-2 border border-white/20 text-zinc-300 hover:border-yellow-500 hover:text-yellow-500 disabled:opacity-50"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                    <button
                                                        data-testid={`moderation-delete-${item.id}`}
                                                        disabled={isActing}
                                                        onClick={() => {
                                                            if (window.confirm("Eliminare definitivamente?")) doAction(kind, item.id, "delete");
                                                        }}
                                                        title="Elimina definitivamente"
                                                        className="p-2 border border-white/20 text-zinc-300 hover:border-abnormal-red hover:text-abnormal-red disabled:opacity-50"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </section>
                    );
                })}
            </div>
        </div>
    );
}
